// src/components/SubmitTime/fields/MultiSelectField.tsx
// Modern React implementation of MultiSelectField (multi-select) custom field
// Uses react-select v5 with custom styling to match legacy appearance

import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import type { MultiValue, StylesConfig } from 'react-select';
import type { CustomFieldProps, SelectOption } from '../types';
import { FieldTypeId } from '../types';
import { isSubmitTimeDark, ST_DARK } from '../components/selectDarkMode';
import './MultiSelectField.css';

interface MultiSelectFieldProps extends CustomFieldProps {
  autoFocus?: boolean;
}

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  customField,
  canEdit,
  configValueId,
  layerId,
  projectId,
  handleDynamicInputChange,
  UpdateCustomFields,
  autoFocus,
  showRequiredError,
}) =>
{
  const [selectedValues, setSelectedValues] = useState<SelectOption[]>([]);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load options: if ServiceUrl is present call the API, otherwise use inline Options
  useEffect(() =>
  {
    const serviceUrl = (customField as any).ServiceUrl as string | undefined;
    if (serviceUrl)
    {
      // Strip leading "api/" since apiService base URL already includes it
      let url = serviceUrl.replace(/^api\//, '');

      // FieldTypeId 7 = person lookup: append context params so the API can filter by layer/project
      // Other types (8, 9, etc.) use a ServiceUrl that already contains its own query params — leave as-is
      if (customField.FieldTypeId === FieldTypeId.Date)
      {
        const params = new URLSearchParams();
        if (configValueId !== undefined) params.append('configValueId', String(configValueId));
        if (layerId !== undefined) params.append('layerId', String(layerId));
        if (projectId !== undefined && projectId !== null)
          params.append('projectId', String(projectId));
        if (params.toString())
        {
          url = url.includes('?') ? `${url}&${params}` : `${url}?${params}`;
        }
      }

      setIsLoading(true);
      import('@/services/api')
        .then(({ apiService }) => apiService.get<any[]>(url))
        .then(data =>
        {
          const textField = (customField as any).TextField || 'Name';
          const valueField = (customField as any).ValueField || 'Id';
          const opts: SelectOption[] = data.map(item => ({
            value: item[valueField],
            label: item[textField],
          }));
          setOptions(opts);

          // Pre-select values from current Value
          if (customField.Value)
          {
            const values = String(customField.Value).split(',').filter(Boolean);
            setSelectedValues(opts.filter(o => values.includes(String(o.value))));
          }
        })
        .catch(err => console.error('Error loading MultiSelectField options:', err))
        .finally(() => setIsLoading(false));
    }
    else if (customField.Options && Array.isArray(customField.Options))
    {
      const formattedOptions = customField.Options.map(opt => ({
        value: opt.Value,
        label: opt.Text,
      }));
      setOptions(formattedOptions);
      const selected = formattedOptions.filter(opt =>
        customField.Options?.some(o => o.IsSelected && o.Value === opt.value),
      );
      setSelectedValues(selected);
    }
  }, [(customField as any).ServiceUrl, configValueId, layerId, projectId]);

  const handleChange = (newValues: MultiValue<SelectOption>) =>
  {
    const selectedArray = Array.from(newValues);
    setSelectedValues(selectedArray);

    // Send comma-separated values to API; also pass display labels as optional 3rd arg
    const values = selectedArray.map(v => v.value).join(',');
    const labels = selectedArray.map(v => v.label).join(', ');
    handleDynamicInputChange(customField.CustomFieldId, values, labels);
  };

  const handleBlur = () =>
  {
    if (UpdateCustomFields)
    {
      UpdateCustomFields(customField);
    }
  };

  const isFieldDisabled = !canEdit || !customField.ConversationIsEnable;

  // Custom styles to match legacy appearance exactly
  const customStyles: StylesConfig<SelectOption, true> = {
    control: (base, state) => ({
      ...base,
      minHeight: '32px',
      borderRadius: '2px',
      boxShadow: 'none',
      fontSize: '11px',
      ...(isSubmitTimeDark() ? { backgroundColor: ST_DARK.controlBg } : {}),
      borderColor: showRequiredError
        ? '#F26C4F'
        : state.isFocused
          ? (isSubmitTimeDark() ? ST_DARK.borderFocus : '#aaa')
          : (isSubmitTimeDark() ? ST_DARK.border : '#ddd'),
      '&:hover': {
        borderColor: showRequiredError ? '#F26C4F' : (isSubmitTimeDark() ? ST_DARK.borderFocus : '#aaa'),
      },
    }),
    valueContainer: base => ({
      ...base,
      padding: '2px 6px',
    }),
    input: base => ({
      ...base,
      margin: 0,
      padding: 0,
      ...(isSubmitTimeDark() ? { color: ST_DARK.text } : {}),
    }),
    indicatorSeparator: base => ({
      ...base,
      backgroundColor: '#ddd',
    }),
    dropdownIndicator: base => ({
      ...base,
      padding: '0 6px',
      color: '#999',
      '&:hover': {
        color: '#666',
      },
    }),
    clearIndicator: base => ({
      ...base,
      padding: '0 4px',
      color: '#999',
      '&:hover': {
        color: '#666',
      },
    }),
    placeholder: base => ({
      ...base,
      fontSize: '11px',
      color: isSubmitTimeDark() ? ST_DARK.placeholder : '#aaa',
    }),
    multiValue: base => ({
      ...base,
      backgroundColor: isSubmitTimeDark() ? ST_DARK.menuBg : '#fff',
      borderRadius: '2px',
      border: `1px solid ${isSubmitTimeDark() ? ST_DARK.border : '#ddd'}`,
      fontSize: '11px',
      flexDirection: 'row-reverse' as const,
    }),
    multiValueLabel: base => ({
      ...base,
      color: isSubmitTimeDark() ? ST_DARK.text : 'rgb(158, 149, 135)',
      fontFamily: "'Roboto', sans-serif",
      padding: '1px 5px',
      fontSize: '11px',
    }),
    multiValueRemove: base => ({
      ...base,
      color: '#999',
      borderRight: `1px solid ${isSubmitTimeDark() ? ST_DARK.border : '#ddd'}`,
      paddingLeft: '4px',
      paddingRight: '4px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: isSubmitTimeDark() ? '#4a4c4c' : '#f5f5f5',
        color: isSubmitTimeDark() ? '#e5e7eb' : '#333',
      },
    }),
    menu: base => ({
      ...base,
      marginTop: 0,
      borderRadius: 0,
      maxHeight: '200px',
      ...(isSubmitTimeDark()
        ? { backgroundColor: ST_DARK.menuBg, border: `1px solid ${ST_DARK.border}` }
        : {}),
    }),
    menuList: base => ({
      ...base,
      padding: 0,
      maxHeight: '198px',
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '11px',
      lineHeight: '1.8',
      padding: '4px 10px',
      backgroundColor: state.isFocused
        ? '#62accb'
        : state.isSelected
          ? (isSubmitTimeDark() ? '#2d6a7e' : '#f5faff')
          : (isSubmitTimeDark() ? ST_DARK.menuBg : '#fff'),
      color: state.isFocused
        ? '#fff'
        : (isSubmitTimeDark() ? ST_DARK.optionText : '#666666'),
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#62accb',
      },
    }),
  };

  return (
    <div className="corgee_com_custom-fields-multiselect">
      <Select<SelectOption, true>
        value={selectedValues}
        onChange={handleChange}
        onBlur={handleBlur}
        options={options}
        isMulti
        isDisabled={isFieldDisabled}
        isClearable={!isFieldDisabled}
        isSearchable={true}
        isLoading={isLoading}
        placeholder={customField.Placeholder || 'Select...'}
        styles={customStyles}
        classNamePrefix="Select"
        menuPlacement="auto"
        menuPosition="fixed"
        closeMenuOnSelect={false}
        autoFocus={autoFocus}
        openMenuOnFocus={autoFocus}
      />
    </div>
  );
};

export default MultiSelectField;
